import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Buildings } from "@phosphor-icons/react";

const empty = {
  landlord_id: "",
  address: "",
  eircode: "",
  property_type: "Apartment",
  ber_rating: "",
  furnished: "Unfurnished",
  bedrooms: 1,
  bathrooms: 1,
  heating_type: "Gas",
  parking: "None",
  notes: "",
};

const TYPES = ["Apartment", "House", "Duplex", "Studio", "Townhouse", "Bungalow"];
const BER = ["A1", "A2", "A3", "B1", "B2", "B3", "C1", "C2", "C3", "D1", "D2", "E1", "E2", "F", "G"];
const HEAT = ["Gas", "Oil", "Electric", "Heat Pump", "Solid Fuel", "District"];
const PARK = ["None", "On-Street", "Driveway", "Garage", "Underground"];
const FURN = ["Furnished", "Part-furnished", "Unfurnished"];

export default function Properties() {
  const [items, setItems] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const [p, l] = await Promise.all([client.get("/properties"), client.get("/landlords")]);
    setItems(p.data);
    setLandlords(l.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms) };
      if (editing) {
        await client.put(`/properties/${editing}`, body);
        toast.success("Property updated");
      } else {
        await client.post("/properties", body);
        toast.success("Property added");
      }
      setForm(empty);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error("Failed. Make sure a landlord is selected.");
    }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({ ...empty, ...p });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await client.delete(`/properties/${id}`);
    toast.success("Deleted");
    load();
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const landlordName = (id) => landlords.find((l) => l.id === id)?.name || "—";

  return (
    <div data-testid="properties-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#6B7280] mb-1">Portfolio</div>
          <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#1E3A5F]">
            Properties
          </h1>
        </div>
        <button
          onClick={() => {
            if (!landlords.length) {
              toast.error("Add a landlord first.");
              return;
            }
            setForm({ ...empty, landlord_id: landlords[0].id });
            setEditing(null);
            setShowForm(true);
          }}
          className="trent-btn-primary"
          data-testid="add-property-btn"
        >
          <Plus weight="bold" size={16} /> Add property
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="trent-card p-6 mb-8 grid md:grid-cols-3 gap-4" data-testid="property-form">
          <Field label="Landlord *">
            <select required value={form.landlord_id} onChange={upd("landlord_id")} className="trent-input" data-testid="property-landlord">
              <option value="">Select a landlord</option>
              {landlords.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} {l.company_name ? `· ${l.company_name}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Address *" wide>
            <input required value={form.address} onChange={upd("address")} className="trent-input" data-testid="property-address" />
          </Field>
          <Field label="Eircode">
            <input value={form.eircode} onChange={upd("eircode")} placeholder="e.g. D02 XY45" className="trent-input" data-testid="property-eircode" />
          </Field>
          <Field label="Type">
            <select value={form.property_type} onChange={upd("property_type")} className="trent-input">
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="BER Rating">
            <select value={form.ber_rating} onChange={upd("ber_rating")} className="trent-input">
              <option value="">—</option>
              {BER.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Furnished">
            <select value={form.furnished} onChange={upd("furnished")} className="trent-input">
              {FURN.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Bedrooms">
            <input type="number" min="0" value={form.bedrooms} onChange={upd("bedrooms")} className="trent-input" />
          </Field>
          <Field label="Bathrooms">
            <input type="number" min="0" value={form.bathrooms} onChange={upd("bathrooms")} className="trent-input" />
          </Field>
          <Field label="Heating">
            <select value={form.heating_type} onChange={upd("heating_type")} className="trent-input">
              {HEAT.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Parking">
            <select value={form.parking} onChange={upd("parking")} className="trent-input">
              {PARK.map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Notes" wide>
            <textarea value={form.notes} onChange={upd("notes")} rows={2} className="trent-input" />
          </Field>
          <div className="md:col-span-3 flex justify-end gap-3">
            <button type="button" onClick={() => setShowForm(false)} className="trent-btn-secondary">
              Cancel
            </button>
            <button type="submit" className="trent-btn-primary" data-testid="property-save-btn">
              {editing ? "Save" : "Add property"}
            </button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center text-[#6B7280] border border-dashed border-[#E5E7EB] py-16 rounded-md">
            No properties yet.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} className="trent-card p-5 hover:border-[#1E3A5F]/40 transition-colors" data-testid={`property-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-9 h-9 bg-[#1E3A5F] flex items-center justify-center rounded-sm text-white">
                <Buildings size={18} weight="duotone" />
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit(p)} className="p-1.5 hover:bg-[#F3F4F6] rounded-sm" data-testid={`edit-property-${p.id}`}>
                  <PencilSimple size={14} />
                </button>
                <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-[#FEE2E2] text-[#991B1B] rounded-sm" data-testid={`delete-property-${p.id}`}>
                  <Trash size={14} />
                </button>
              </div>
            </div>
            <div className="mt-3 font-semibold text-[#1E3A5F] leading-snug">{p.address}</div>
            <div className="text-xs text-[#6B7280] mt-1">
              {p.eircode || "—"} · {landlordName(p.landlord_id)}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
              <Tag>{p.property_type}</Tag>
              <Tag>{p.bedrooms} bed</Tag>
              <Tag>{p.bathrooms} bath</Tag>
              {p.ber_rating && <Tag accent>BER {p.ber_rating}</Tag>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children, wide }) => (
  <label className={`block ${wide ? "md:col-span-2" : ""}`}>
    <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Tag = ({ children, accent }) => (
  <span
    className={`px-2 py-1 rounded-sm border font-semibold uppercase tracking-wider ${
      accent ? "bg-[#DCFCE7] text-[#14532D] border-[#86EFAC]" : "bg-[#F3F4F6] text-[#111827] border-[#E5E7EB]"
    }`}
  >
    {children}
  </span>
);
